import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Check,
  CircleDot,
  LayoutGrid,
  Loader2,
  Plus,
  Reply,
  Shapes,
  Target,
  Timer,
} from 'lucide-react';
import { toast } from 'sonner';
import { ticketApi } from '@/lib/api';
import { formatAdminBusinessEt } from '@/lib/admin-datetime';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { Ticket, TicketCategory, TicketStatus } from '@/types/ticket';
import {
  TICKET_CATEGORY_LABELS,
  TICKET_STATUS_LABELS,
} from '@/components/tickets/ticketLabels';

type TicketWithEnrollment = Ticket & {
  enrollment?: {
    student?: { fullName?: string; email?: string; phone?: string };
    programName?: string | null;
    programStartDate?: string | null;
  };
};

type DashboardStats = {
  totalTickets: number;
  weekOverWeekPct: number;
  status: Record<string, number>;
  categories: Record<string, number>;
};

const STATUS_ORDER: TicketStatus[] = ['just_created', 'in_process', 'replied', 'closed'];
const CATEGORY_ORDER: TicketCategory[] = ['login', 'program', 'billing', 'general', 'others'];

const STATUS_NOTES: Record<TicketStatus, string> = {
  just_created: 'New requests awaiting review',
  in_process: 'Admin marked as in process',
  replied: 'Admin has responded',
  closed: 'Resolved tickets',
};

function statusBadgeClass(status: TicketStatus): string {
  switch (status) {
    case 'just_created':
      return 'bg-info/10 text-info';
    case 'in_process':
      return 'bg-success/10 text-success';
    case 'replied':
      return 'bg-warning/10 text-warning';
    case 'closed':
      return 'bg-muted text-muted-foreground';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

function MetricIcon({ children }: { children: React.ReactNode }) {
  return (
    <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary/10 text-primary">
      {children}
    </span>
  );
}

type AdminTicketsDashboardProps = {
  onOpenTicket: (ticketId: string) => void;
};

export function AdminTicketsDashboard({ onOpenTicket }: AdminTicketsDashboardProps) {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [tableTickets, setTableTickets] = useState<TicketWithEnrollment[]>([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<TicketStatus | ''>('');
  const [filterCategory, setFilterCategory] = useState<TicketCategory | ''>('');
  const [filterDate, setFilterDate] = useState('');

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await ticketApi.getStats();
      const status: Record<string, number> = {};
      for (const s of res.data.statusStats ?? []) status[s._id] = s.count;
      const categories: Record<string, number> = {};
      for (const c of res.data.categoryStats ?? []) categories[c._id] = c.count;
      setStats({
        totalTickets: res.data.totalTickets ?? 0,
        weekOverWeekPct: (res.data as { weekOverWeekPct?: number }).weekOverWeekPct ?? 0,
        status,
        categories,
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load stats');
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadTable = useCallback(async () => {
    setTableLoading(true);
    try {
      const res = await ticketApi.getTickets({
        limit: 100,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        ...(search.trim() ? { search: search.trim() } : {}),
        ...(filterStatus ? { status: filterStatus } : {}),
        ...(filterCategory ? { category: filterCategory } : {}),
        ...(filterDate ? { createdFrom: filterDate, createdTo: filterDate } : {}),
      });
      setTableTickets((res.data.tickets ?? []) as TicketWithEnrollment[]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load tickets');
    } finally {
      setTableLoading(false);
    }
  }, [search, filterStatus, filterCategory, filterDate]);

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  useEffect(() => {
    void loadTable();
  }, [loadTable]);

  const statusTotal = useMemo(() => {
    if (!stats) return 0;
    return STATUS_ORDER.reduce((sum, k) => sum + (stats.status[k] ?? 0), 0);
  }, [stats]);

  const clearFilters = () => {
    setSearch('');
    setFilterStatus('');
    setFilterCategory('');
    setFilterDate('');
  };

  const wowPct = stats?.weekOverWeekPct ?? 0;
  const wowLabel =
    wowPct > 0 ? `+${wowPct}%` : wowPct < 0 ? `${wowPct}%` : '0%';

  return (
    <div className="space-y-5">
      {/* Metric cards */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <div className="min-h-[148px] rounded-3xl border bg-card p-5 shadow-sm">
          <div className="mb-5 flex items-center justify-between gap-3">
            <span className="text-sm font-bold text-muted-foreground">Total Tickets</span>
            <MetricIcon>
              <Target className="h-4 w-4" />
            </MetricIcon>
          </div>
          <p className="text-3xl font-black tracking-tight">
            {statsLoading ? '—' : (stats?.totalTickets ?? 0)}
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            <strong
              className={cn(
                'font-bold',
                wowPct >= 0 ? 'text-success' : 'text-destructive'
              )}
            >
              {wowLabel}
            </strong>{' '}
            from last week
          </p>
        </div>

        {(
          [
            { key: 'just_created' as const, icon: <Plus className="h-4 w-4" /> },
            { key: 'in_process' as const, icon: <Timer className="h-4 w-4" /> },
            { key: 'replied' as const, icon: <Reply className="h-4 w-4" /> },
            { key: 'closed' as const, icon: <Check className="h-4 w-4" /> },
          ] as const
        ).map(({ key, icon }) => (
          <div key={key} className="min-h-[148px] rounded-3xl border bg-card p-5 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-3">
              <span className="text-sm font-bold text-muted-foreground">
                {TICKET_STATUS_LABELS[key]}
              </span>
              <MetricIcon>{icon}</MetricIcon>
            </div>
            <p className="text-3xl font-black tracking-tight">
              {statsLoading ? '—' : (stats?.status[key] ?? 0)}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">{STATUS_NOTES[key]}</p>
          </div>
        ))}
      </div>

      {/* Analytics split */}
      <div className="grid gap-4 lg:grid-cols-[1.2fr_1fr]">
        <div className="overflow-hidden rounded-3xl border bg-card shadow-sm">
          <div className="border-b px-6 py-5">
            <div className="flex items-center gap-2.5 text-lg font-black">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary/10 text-primary">
                <LayoutGrid className="h-4 w-4" />
              </span>
              Status Breakdown
            </div>
            <p className="mt-1 text-sm text-muted-foreground">Current operational ticket status.</p>
          </div>
          <div className="space-y-4 px-6 py-5">
            {STATUS_ORDER.map((status) => {
              const count = stats?.status[status] ?? 0;
              const pct = statusTotal > 0 ? Math.round((count / statusTotal) * 100) : 0;
              return (
                <div key={status} className="grid grid-cols-[120px_1fr_36px] items-center gap-3">
                  <span className="text-sm font-semibold">{TICKET_STATUS_LABELS[status]}</span>
                  <div className="h-2.5 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <strong className="text-right text-sm">{count}</strong>
                </div>
              );
            })}
          </div>
        </div>

        <div className="overflow-hidden rounded-3xl border bg-card shadow-sm">
          <div className="border-b px-6 py-5">
            <div className="flex items-center gap-2.5 text-lg font-black">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary/10 text-primary">
                <Shapes className="h-4 w-4" />
              </span>
              Categories
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Tickets grouped by student issue type.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 p-5 sm:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3">
            {CATEGORY_ORDER.map((cat) => (
              <div
                key={cat}
                className="flex flex-col gap-2 rounded-2xl border bg-background px-4 py-4"
              >
                <strong className="text-sm">{TICKET_CATEGORY_LABELS[cat]}</strong>
                <span className="text-2xl font-black tracking-tight">
                  {statsLoading ? '—' : (stats?.categories[cat] ?? 0)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* All tickets table */}
      <div className="overflow-hidden rounded-3xl border bg-card shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b px-6 py-5">
          <div>
            <div className="flex items-center gap-2.5 text-lg font-black">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary/10 text-primary">
                <CircleDot className="h-4 w-4" />
              </span>
              All Tickets
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Search tickets by student, ticket number, program, dates, or category.
            </p>
          </div>
        </div>

        <div className="grid gap-2 border-b bg-muted/20 px-6 py-4 lg:grid-cols-[2fr_repeat(3,minmax(130px,1fr))_auto]">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, phone, ticket #, program..."
            className="h-10 rounded-xl"
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as TicketStatus | '')}
            className="h-10 rounded-xl border bg-background px-3 text-sm"
          >
            <option value="">All Status</option>
            {STATUS_ORDER.map((s) => (
              <option key={s} value={s}>
                {TICKET_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value as TicketCategory | '')}
            className="h-10 rounded-xl border bg-background px-3 text-sm"
          >
            <option value="">All Categories</option>
            {CATEGORY_ORDER.map((c) => (
              <option key={c} value={c}>
                {TICKET_CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
          <Input
            type="date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            className="h-10 rounded-xl"
          />
          <Button type="button" variant="outline" className="h-10 rounded-xl" onClick={clearFilters}>
            Clear
          </Button>
        </div>

        <div className="overflow-x-auto">
          {tableLoading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading tickets…
            </div>
          ) : tableTickets.length === 0 ? (
            <p className="py-16 text-center text-sm text-muted-foreground">No tickets match your filters.</p>
          ) : (
            <table className="min-w-[1100px] w-full border-collapse text-sm">
              <thead>
                <tr className="border-b bg-muted/20 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">Ticket #</th>
                  <th className="px-4 py-3 font-semibold">Issue</th>
                  <th className="px-4 py-3 font-semibold">Student</th>
                  <th className="px-4 py-3 font-semibold">Program</th>
                  <th className="px-4 py-3 font-semibold">Start Date</th>
                  <th className="px-4 py-3 font-semibold">Category</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Created</th>
                  <th className="px-4 py-3 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {tableTickets.map((t) => {
                  const studentName =
                    t.enrollment?.student?.fullName ?? t.createdBy?.userName ?? '—';
                  const studentEmail =
                    t.enrollment?.student?.email ?? t.createdBy?.userEmail ?? '';
                  const program = t.enrollment?.programName ?? '—';
                  const startDate = t.enrollment?.programStartDate
                    ? formatAdminBusinessEt(String(t.enrollment.programStartDate))
                    : '—';
                  const created = t.timestamps?.createdAt
                    ? formatAdminBusinessEt(t.timestamps.createdAt)
                    : '—';

                  return (
                    <tr key={t.ticketId} className="border-b hover:bg-muted/30">
                      <td className="px-4 py-3 font-medium">{t.ticketId}</td>
                      <td className="max-w-[220px] px-4 py-3">
                        <strong className="line-clamp-1 block font-bold">{t.title}</strong>
                        <span className="line-clamp-1 text-xs text-muted-foreground">
                          {t.lastMessagePreview || t.description}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <strong className="block text-sm">{studentName}</strong>
                        {studentEmail && (
                          <span className="text-xs text-muted-foreground">{studentEmail}</span>
                        )}
                      </td>
                      <td className="max-w-[160px] truncate px-4 py-3">{program}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-xs">{startDate}</td>
                      <td className="px-4 py-3">
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-bold text-primary">
                          {TICKET_CATEGORY_LABELS[t.category] ?? t.category}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            'rounded-full px-2 py-0.5 text-xs font-bold',
                            statusBadgeClass(t.status)
                          )}
                        >
                          {TICKET_STATUS_LABELS[t.status] ?? t.status}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-xs">{created}</td>
                      <td className="px-4 py-3">
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          className="rounded-full"
                          onClick={() => onOpenTicket(t.ticketId)}
                        >
                          Open
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
